import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Check, 
  Copy, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Wrench, 
  ShieldCheck, 
  PenTool, 
  AlertTriangle, 
  Loader2, 
  Building2, 
  Home, 
  Monitor, 
  Send, 
  Info,
  Calendar,
  Sparkles
} from 'lucide-react';
import { createServiceQuote } from '../lib/dbService';
import { TurnstileWidget, type TurnstileWidgetRef } from './TurnstileWidget';

export interface ServiceQuoteRequestProps {
  onBack: () => void;
  onViewTerms?: () => void;
  initialProblem?: string;
  initialServiceMode?: 'onsite' | 'pickup' | 'remote';
}

export const ServiceQuoteRequest: React.FC<ServiceQuoteRequestProps> = ({
  onBack,
  onViewTerms,
  initialProblem = '',
  initialServiceMode = 'onsite'
}) => {
  // Contact State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('Licking');
  const [zipCode, setZipCode] = useState('65542');
  const [addressType, setAddressType] = useState<'business' | 'home'>('home');
  const [address, setAddress] = useState('');

  // Service Details State
  const [serviceMode, setServiceMode] = useState<'onsite' | 'pickup' | 'remote'>(initialServiceMode);
  const [availability, setAvailability] = useState('Flexible / Weekdays');
  const [urgency, setUrgency] = useState('Standard');
  const [whenNeeded, setWhenNeeded] = useState('Within 2-3 Days');
  const [problem, setProblem] = useState(initialProblem);

  // Digital Signature
  const [clientSignature, setClientSignature] = useState('');
  const [signatureDate] = useState(() => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
  const [signatureError, setSignatureError] = useState(false);

  // Turnstile & Submission
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copiedSummary, setCopiedSummary] = useState(false);

  // Update initial values if props change
  useEffect(() => {
    if (initialProblem) setProblem(initialProblem);
  }, [initialProblem]);

  useEffect(() => {
    if (initialServiceMode) setServiceMode(initialServiceMode);
  }, [initialServiceMode]);

  const generateQuoteSummary = () => {
    const fullName = `${firstName} ${lastName}`.trim();
    return `=== SERVICE & REPAIR QUOTE REQUEST ===
CONTACT INFORMATION:
• Client Name: ${fullName || 'Not specified'}
• Phone Number: ${phone || 'Not specified'}
• Email Address: ${email || 'Not specified'}
• Location / City: ${location || 'Not specified'}, Zip: ${zipCode || 'Not specified'}
• Address: ${address || 'Not specified'} (${addressType === 'business' ? 'Commercial / Business' : 'Residential / Home'})

SERVICE SCOPE & PREFERENCES:
• Preferred Service Mode: ${
      serviceMode === 'onsite' 
        ? 'On-Site Support Visit' 
        : serviceMode === 'pickup' 
        ? 'In-Shop / Bench Drop-Off' 
        : 'Remote Desktop Support'
    }
• Availability: ${availability}
• Urgency Level: ${urgency}
• Desired Timeline: ${whenNeeded}

PROBLEM DESCRIPTION & DETAILS:
${problem || 'No specific description provided'}

POLICY ACKNOWLEDGEMENT & AUTHORIZATION:
Client acknowledges standard service policies: 1-hour minimum charge (or 30-min minimum for remote), billed in 30-minute increments thereafter.
• Digital Signature: ${clientSignature}
• Authorization Date: ${signatureDate}`;
  };

  const handleCopySummary = () => {
    navigator.clipboard.writeText(generateQuoteSummary());
    setCopiedSummary(true);
    setTimeout(() => setCopiedSummary(false), 2500);
  };

  const handleEmailDirectBackup = () => {
    const fullName = `${firstName} ${lastName}`.trim() || 'New Client';
    const subject = encodeURIComponent(`Service Request - ${fullName} (${location})`);
    const body = encodeURIComponent(`${generateQuoteSummary()}\n\n(Sent via Randon Brown, LLC direct mailto link)`);
    window.location.href = `mailto:help@randonbrown.com?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !problem.trim()) {
      setSubmitError('Please complete all required fields (Name, Email Address, Phone Number, and Problem Description).');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSubmitError('Please enter a valid email address (e.g., name@example.com).');
      return;
    }

    if (!clientSignature.trim()) {
      setSignatureError(true);
      const sigElement = document.getElementById('quote-signature-box');
      if (sigElement) {
        sigElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!turnstileToken) {
      setSubmitError('Please complete the quick security check below to verify your request.');
      return;
    }

    setSignatureError(false);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await createServiceQuote({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        location: location.trim(),
        zipCode: zipCode.trim(),
        addressType,
        address: address.trim(),
        serviceMode,
        availability,
        urgency,
        whenNeeded,
        problem: problem.trim(),
        clientSignature: clientSignature.trim(),
        signatureDate,
        turnstileToken: turnstileToken || undefined,
        source: 'web_form'
      });

      setIsSubmitting(false);

      if (result.success && result.id) {
        setSubmissionId(result.id);
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(result.error || 'Failed to submit quote request. Please try again or use direct email.');
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Network error during submission.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto px-3 sm:px-6 py-4 text-left">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 text-brand-400" />
          <span>Back</span>
        </button>
      </div>

      {isSubmitted ? (
        /* Success Screen */
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-6 sm:p-10 rounded-3xl bg-neutral-950 border border-emerald-500/30 text-center space-y-6 shadow-2xl"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-9 h-9" />
          </div>

          <div className="space-y-2">
            <span className="text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
              Quote Request Logged &amp; Dispatched
            </span>
            <h1 className="text-2xl sm:text-3xl font-display font-black text-white">
              Thank You, {firstName}!
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-lg mx-auto leading-relaxed">
              Your service request has been securely recorded and sent to Randon Brown. You will receive a confirmation call or email shortly.
            </p>
          </div>

          {/* Reference Receipt Box */}
          <div className="p-4 sm:p-5 rounded-2xl bg-neutral-900/70 border border-neutral-800 text-left max-w-md mx-auto space-y-3 text-xs">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2.5">
              <span className="font-mono text-slate-400">Reference Number</span>
              <span className="font-mono font-bold text-brand-400 text-sm">
                #{submissionId ? submissionId.slice(0, 8).toUpperCase() : 'REQ-DIRECT'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <span className="text-slate-500 block font-mono uppercase">Client</span>
                <span className="text-white font-medium">{firstName} {lastName}</span>
              </div>
              <div>
                <span className="text-slate-500 block font-mono uppercase">Location</span>
                <span className="text-white font-medium">{location}, {zipCode}</span>
              </div>
              <div>
                <span className="text-slate-500 block font-mono uppercase">Phone</span>
                <span className="text-white font-medium">{phone}</span>
              </div>
              <div>
                <span className="text-slate-500 block font-mono uppercase">Service Mode</span>
                <span className="text-brand-300 font-medium capitalize">{serviceMode}</span>
              </div>
            </div>

            <div className="pt-2 border-t border-neutral-800">
              <span className="text-slate-500 block font-mono uppercase text-[10px]">Digital Signature</span>
              <span className="text-brand-300 italic font-serif text-sm">{clientSignature}</span>
              <span className="text-slate-500 text-[10px] block mt-0.5">{signatureDate}</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto pt-2">
            <button
              type="button"
              onClick={onBack}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider cursor-pointer transition-all active:scale-95"
            >
              Return to Site
            </button>
            <button
              type="button"
              onClick={handleCopySummary}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold cursor-pointer transition-all flex items-center justify-center gap-1.5"
            >
              {copiedSummary ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 text-brand-400" />
                  <span>Copy Summary</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      ) : (
        /* Main Request Form */
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header Banner */}
          <div className="text-center max-w-xl mx-auto space-y-1.5">
            <div className="inline-flex items-center gap-1.5 py-0.5 px-3 rounded-full bg-brand-500/10 text-brand-400 text-[11px] font-mono font-bold uppercase tracking-wider border border-brand-500/20">
              <Wrench className="w-3 h-3" />
              Service &amp; Repair Intake
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-black text-white tracking-tight">
              Request a Service Quote
            </h1>
            <p className="text-xs text-slate-400 leading-relaxed">
              Describe your issue or technical needs. We offer upfront pricing, standard 1-hour minimums, and 30-minute incremental billing thereafter.
            </p>
          </div>

          {/* Service Mode Selector */}
          <div className="p-4 sm:p-5 rounded-2xl bg-neutral-950/90 border border-neutral-800 space-y-3">
            <label className="text-xs font-mono font-bold text-white block">
              1. Preferred Service Mode
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Option 1: On-Site */}
              <div
                onClick={() => setServiceMode('onsite')}
                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                  serviceMode === 'onsite'
                    ? 'bg-brand-500/10 border-brand-500 text-white shadow-md'
                    : 'bg-neutral-900/60 border-neutral-800 text-slate-300 hover:border-neutral-700'
                }`}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <MapPin className="w-4 h-4 text-brand-400" />
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      serviceMode === 'onsite' ? 'border-brand-400 bg-brand-400 text-neutral-950' : 'border-neutral-700'
                    }`}>
                      {serviceMode === 'onsite' && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </div>
                  </div>
                  <h4 className="text-xs font-bold text-white">On-Site Visit</h4>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    Technician travels directly to your home or office.
                  </p>
                </div>
                <span className="text-[10px] font-mono text-brand-400 font-semibold mt-2 pt-1.5 border-t border-neutral-800/80 block">
                  $75–$150/hr by location
                </span>
              </div>

              {/* Option 2: In-Shop / Bench */}
              <div
                onClick={() => setServiceMode('pickup')}
                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                  serviceMode === 'pickup'
                    ? 'bg-emerald-500/10 border-emerald-500 text-white shadow-md'
                    : 'bg-neutral-900/60 border-neutral-800 text-slate-300 hover:border-neutral-700'
                }`}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Wrench className="w-4 h-4 text-emerald-400" />
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      serviceMode === 'pickup' ? 'border-emerald-400 bg-emerald-400 text-neutral-950' : 'border-neutral-700'
                    }`}>
                      {serviceMode === 'pickup' && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </div>
                  </div>
                  <h4 className="text-xs font-bold text-white">In-Shop / Bench</h4>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    Drop off your desktop, laptop, or phone at our bench.
                  </p>
                </div>
                <span className="text-[10px] font-mono text-emerald-400 font-semibold mt-2 pt-1.5 border-t border-neutral-800/80 block">
                  $75/hr Flat Bench Rate
                </span>
              </div>

              {/* Option 3: Remote Desktop */}
              <div
                onClick={() => setServiceMode('remote')}
                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between ${
                  serviceMode === 'remote'
                    ? 'bg-violet-500/10 border-violet-500 text-white shadow-md'
                    : 'bg-neutral-900/60 border-neutral-800 text-slate-300 hover:border-neutral-700'
                }`}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Monitor className="w-4 h-4 text-violet-400" />
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      serviceMode === 'remote' ? 'border-violet-400 bg-violet-400 text-neutral-950' : 'border-neutral-700'
                    }`}>
                      {serviceMode === 'remote' && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </div>
                  </div>
                  <h4 className="text-xs font-bold text-white">Remote Support</h4>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    Instant screen share assistance anywhere with internet.
                  </p>
                </div>
                <span className="text-[10px] font-mono text-violet-400 font-semibold mt-2 pt-1.5 border-t border-neutral-800/80 block">
                  $37.50 / 30 min (30m min)
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-neutral-950/90 border border-neutral-800 space-y-3">
            <div className="flex items-center gap-2 text-white font-display font-bold text-xs sm:text-sm">
              <Phone className="w-4 h-4 text-brand-400" />
              <span>2. Contact Information</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                  <span className="text-brand-400">*</span> First Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="John"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                  <span className="text-brand-400">*</span> Last Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="Smith"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                  <span className="text-brand-400">*</span> Phone Number
                </label>
                <input
                  type="tel"
                  required
                  placeholder="(314) 000-0000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                  <span className="text-brand-400">*</span> <Mail className="w-3 h-3 text-brand-400" /> Email Address
                </label>
                <input
                  type="email"
                  required
                  placeholder="john@example.com"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (submitError) setSubmitError(null);
                  }}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-brand-400" /> City / Town
                </label>
                <input
                  type="text"
                  placeholder="e.g., Licking, Houston, Rolla"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                  Zip Code
                </label>
                <input
                  type="text"
                  placeholder="65542"
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                />
              </div>

              {serviceMode === 'onsite' && (
                <>
                  <div className="sm:col-span-2">
                    <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                      Street Address or Landmark
                    </label>
                    <input
                      type="text"
                      placeholder="123 Main St, Apt / Suite #, or crossroads"
                      value={address}
                      onChange={e => setAddress(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600"
                    />
                  </div>

                  <div className="sm:col-span-2 flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                      <input
                        type="radio"
                        name="addressType"
                        checked={addressType === 'home'}
                        onChange={() => setAddressType('home')}
                        className="text-brand-500 focus:ring-brand-500"
                      />
                      <Home className="w-3.5 h-3.5 text-slate-400" />
                      <span>Residential / Home</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                      <input
                        type="radio"
                        name="addressType"
                        checked={addressType === 'business'}
                        onChange={() => setAddressType('business')}
                        className="text-brand-500 focus:ring-brand-500"
                      />
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span>Commercial / Business</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Problem & Urgency Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-neutral-950/90 border border-neutral-800 space-y-3">
            <div className="flex items-center gap-2 text-white font-display font-bold text-xs sm:text-sm">
              <Wrench className="w-4 h-4 text-brand-400" />
              <span>3. Service Details &amp; Problem Description</span>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                  <span className="text-brand-400">*</span> What needs repair or technical setup?
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Describe your device, symptoms, error messages, or goals (e.g. computer running slow, virus removal, network cabling, Starlink setup, etc.)"
                  value={problem}
                  onChange={e => setProblem(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-600 leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                    When is it needed?
                  </label>
                  <select
                    value={whenNeeded}
                    onChange={e => setWhenNeeded(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs text-slate-200 cursor-pointer"
                  >
                    <option value="ASAP / Emergency">ASAP / Emergency</option>
                    <option value="Within 2-3 Days">Within 2-3 Days</option>
                    <option value="This Week">This Week</option>
                    <option value="Within 2 Weeks">Within 2 Weeks</option>
                    <option value="Flexible / Planning Ahead">Flexible / Planning Ahead</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                    Urgency Level
                  </label>
                  <select
                    value={urgency}
                    onChange={e => setUrgency(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs text-slate-200 cursor-pointer"
                  >
                    <option value="Standard">Standard</option>
                    <option value="Urgent">Urgent / Priority</option>
                    <option value="Critical Outage">Critical Outage / Down</option>
                    <option value="Low / Flexible">Low / Flexible</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                    Your Availability
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Weekday mornings, after 4pm"
                    value={availability}
                    onChange={e => setAvailability(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs text-white placeholder-slate-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Policy Acknowledgement & Digital Signature */}
          <div 
            id="quote-signature-box"
            className={`p-4 sm:p-5 rounded-2xl bg-neutral-950/90 border ${
              signatureError 
                ? 'border-red-500/80 shadow-[0_0_20px_rgba(239,68,68,0.25)]' 
                : 'border-brand-500/30'
            } space-y-3 transition-all`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <PenTool className="w-4 h-4 text-brand-400" />
                <h3 className="text-xs sm:text-sm font-bold text-white font-display">
                  Digital Authorization &amp; Policy Acknowledgement
                </h3>
              </div>
              <span className="text-[11px] font-mono text-slate-400">
                {signatureDate}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800/80 text-xs text-slate-300 leading-relaxed space-y-1">
              <p>
                <strong className="text-brand-400 font-semibold">Standard Service Policy:</strong> Work is billed with a standard 1-hour minimum charge (or 30-min minimum for remote desktop), and in 30-minute increments thereafter rounded up. Travel is included in regional rates.
              </p>
              {onViewTerms && (
                <button
                  type="button"
                  onClick={onViewTerms}
                  className="text-brand-400 hover:text-brand-300 underline font-semibold cursor-pointer text-[11px] pt-0.5 inline-block"
                >
                  View Complete Service Terms &amp; Conditions &rarr;
                </button>
              )}
            </div>

            <div>
              <label className="text-[11px] font-mono font-bold text-slate-300 block mb-1">
                <span className="text-brand-400">*</span> Type Full Name to Authorize Quote / Work Request
              </label>
              <input
                type="text"
                required
                placeholder="e.g., John M. Smith"
                value={clientSignature}
                onChange={e => {
                  setClientSignature(e.target.value);
                  if (e.target.value.trim()) setSignatureError(false);
                }}
                className={`w-full px-3.5 py-2.5 rounded-xl bg-neutral-900 border ${
                  signatureError ? 'border-red-500' : 'border-neutral-800 focus:border-brand-500'
                } focus:outline-none text-white placeholder-slate-500 font-serif italic text-base`}
              />
              {signatureError && (
                <p className="text-xs font-medium text-red-400 pt-1">
                  Please type your name as a digital signature before submitting.
                </p>
              )}
            </div>

            {/* Turnstile Security Widget */}
            <div className="pt-2">
              <TurnstileWidget
                ref={turnstileRef}
                siteKey="0x4AAAAAAFLhILYKdp64I0Y2"
                action="contact"
                onSuccess={(token) => {
                  setTurnstileToken(token);
                  setSubmitError(null);
                }}
                onError={() => {
                  setTurnstileToken(null);
                }}
                onExpire={() => {
                  setTurnstileToken(null);
                }}
              />
            </div>
          </div>

          {/* Submission Error Alert */}
          {submitError && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                <span>{submitError}</span>
              </div>
              <button
                type="button"
                onClick={handleEmailDirectBackup}
                className="underline text-red-200 hover:text-white font-medium cursor-pointer shrink-0"
              >
                Email Instead
              </button>
            </div>
          )}

          {/* Submit Action */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3">
            <button
              type="submit"
              disabled={isSubmitting || !turnstileToken}
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-gradient-to-r from-brand-500 to-amber-500 hover:from-brand-400 hover:to-amber-400 text-neutral-950 font-extrabold text-xs uppercase tracking-wider font-mono flex items-center justify-center gap-2 shadow-lg shadow-brand-500/20 active:scale-95 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting Quote Request...</span>
                </>
              ) : !turnstileToken ? (
                <>
                  <ShieldCheck className="w-4 h-4 opacity-70" />
                  <span>Verify Security Check to Submit</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Service Quote Request</span>
                </>
              )}
            </button>

            <span className="text-[11px] font-mono text-slate-500 text-center sm:text-right">
              Direct inbox alert dispatched to help@randonbrown.com
            </span>
          </div>
        </form>
      )}
    </div>
  );
};

export default ServiceQuoteRequest;
